import { useCallback, useEffect, useState } from "react";
import { getReportAnalytics, exportCampaignStats } from "../../../api/ads";
import type { ReportAnalytics, ReportAnalyticsParams } from "../../../api/ads";

export const useReportAnalytics = (initialParams: ReportAnalyticsParams = {}) => {
  const [params, setParamsState] = useState<ReportAnalyticsParams>(initialParams);
  const [analytics, setAnalytics] = useState<ReportAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(
    async (override?: Partial<ReportAnalyticsParams>) => {
      try {
        setAnalyticsLoading(true);
        setError(null);
        const merged: ReportAnalyticsParams = { ...params, ...override };
        const data = await getReportAnalytics(merged);
        setAnalytics(data);
      } catch (err: any) {
        setError(err?.message || "Failed to load analytics");
      } finally {
        setAnalyticsLoading(false);
      }
    },
    [params]
  );

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const setParams = useCallback((next: Partial<ReportAnalyticsParams>) => {
    setParamsState((prev) => ({ ...prev, ...next }));
  }, []);

  const exportReport = useCallback(async (): Promise<Blob> => {
    try {
      setExporting(true);
      setError(null);
      return await exportCampaignStats({ dateFrom: params.dateFrom, dateTo: params.dateTo });
    } catch (err: any) {
      setError(err?.message || "Failed to export report");
      throw err;
    } finally {
      setExporting(false);
    }
  }, [params.dateFrom, params.dateTo]);

  return {
    functions: { fetchAnalytics, setParams, exportReport },
    values: { analytics, params, error },
    loading: analyticsLoading,
    exporting,
  };
};
